import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import WrongLocationOutlinedIcon from "@mui/icons-material/WrongLocationOutlined";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { useTranslation } from "react-i18next";
import StatsCard from "../../common/New/StatsCard";
import DashboardMapFilter from "../../dashboard/DashboardMapFilter";
import {
  getRouteAdherencePercent,
  getRouteStopCount,
  getTripDelayMinutes,
  isDelayedTrip,
  isOnTimeTrip,
} from "../../route/routeDashboardUtils";
import {
  ChunkLoadingIndicator,
  useProgressiveOverviewData,
} from "../../manageData/Analytics/useProgressiveOverviewData";

const COLORS = {
  blue: "#2867E8",
  green: "#16B364",
  amber: "#F5A000",
  red: "#F04438",
  purple: "#9B51E0",
  cyan: "#06B6D4",
  text: "#172033",
  muted: "#718096",
  track: "#EEF1F5",
};

const number = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const rowsFrom = (state) => {
  const payload = state?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const routeName = (row, index = 0) =>
  row?.routeName ||
  row?.route?.name ||
  row?.route_name ||
  row?.name ||
  `Route ${index + 1}`;

const parseMinutes = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const text = String(value).toLowerCase();
  const days = number(text.match(/([\d.]+)\s*d/)?.[1]);
  const hours = number(text.match(/([\d.]+)\s*h/)?.[1]);
  const minutes = number(text.match(/([\d.]+)\s*m(?!s)/)?.[1]);
  const seconds = number(text.match(/([\d.]+)\s*s/)?.[1]);
  if (days || hours || minutes || seconds) {
    return days * 1440 + hours * 60 + minutes + seconds / 60;
  }
  const clock = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  return clock
    ? number(clock[1]) * 60 + number(clock[2]) + number(clock[3]) / 60
    : number(text);
};

const getDuration = (row) =>
  parseMinutes(
    row?.durationMinutes ??
      row?.tripDuration ??
      row?.totalDuration ??
      row?.duration,
  );

const getMissedStops = (row) =>
  number(
    row?.missedStops ??
      row?.missedStopCount ??
      row?.stopsMissed ??
      row?.skippedStops,
  );

const getDelay = (row) =>
  parseMinutes(
    row?.delayMinutes ?? row?.averageDelay ?? row?.delayDuration ?? row?.delay,
  );

const getDistance = (row) =>
  number(
    row?.distance ??
      row?.distanceKm ??
      row?.distanceCovered ??
      row?.noOfKmsCovered,
  );

const unwrapRoutes = (routesData) => {
  if (Array.isArray(routesData)) return routesData;
  if (Array.isArray(routesData?.data)) return routesData.data;
  if (Array.isArray(routesData?.routes)) return routesData.routes;
  return [];
};

const formatDuration = (minutes) => {
  const rounded = Math.round(minutes || 0);
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (!hours) return `${mins}m`;
  return `${hours}h ${mins}m`;
};

function Panel({ title, action, progress, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #DDE2EA",
        borderRadius: 1.5,
        overflow: "hidden",
        position: "relative",
        minWidth: 0,
        ...sx,
      }}
    >
      <ChunkLoadingIndicator progress={progress} />
      <Box
        sx={{
          minHeight: 44,
          px: 2,
          borderBottom: "1px solid #EEF1F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
          {title}
        </Typography>
        {action && (
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: COLORS.blue }}>
            {action}
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

function ProgressList({ rows, valueKey, suffix = "%", color = COLORS.blue }) {
  const { t } = useTranslation();
  const maxValue = Math.max(...rows.map((row) => number(row[valueKey])), 1);
  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {rows.length ? (
        rows.map((row) => {
          const value = number(row[valueKey]);
          const width =
            suffix === "%" ? Math.min(value, 100) : (value / maxValue) * 100;
          return (
            <Box key={`${row.name}-${valueKey}`}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.7,
                }}
              >
                <Typography sx={{ fontSize: 10, color: COLORS.text }}>
                  {row.name}
                </Typography>
                <Typography
                  sx={{ fontSize: 10, fontWeight: 700, color: COLORS.text }}
                >
                  {Math.round(value * 10) / 10}
                  {suffix}
                </Typography>
              </Box>
              <Box
                sx={{
                  height: 6,
                  borderRadius: 4,
                  bgcolor: COLORS.track,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{ height: "100%", width: `${width}%`, bgcolor: color }}
                />
              </Box>
            </Box>
          );
        })
      ) : (
        <Typography
          sx={{ py: 5, textAlign: "center", fontSize: 11, color: COLORS.muted }}
        >
          {t("No route data for the selected period")}
        </Typography>
      )}
    </Box>
  );
}

export default function RouteAnalytics({
  routesData = [],
  selectedDateRange = "today",
  customDateRange = null,
}) {
  const { t } = useTranslation();
  const { chartDataStates, chunkProgress, error, isCancelling } =
    useProgressiveOverviewData(selectedDateRange, customDateRange);
  const tripState = chartDataStates.trip_vehicles;
  const activityState = chartDataStates.activity_vehicles;
  const runtimeState = chartDataStates.runtime_vehicles;
  const trips = useMemo(() => rowsFrom(tripState), [tripState]);
  const activity = useMemo(
    () => rowsFrom(activityState),
    [activityState],
  );
  const runtime = useMemo(
    () => rowsFrom(runtimeState),
    [runtimeState],
  );
  const routes = useMemo(() => unwrapRoutes(routesData), [routesData]);
  const tripProgress = chunkProgress.trip_vehicles;
  const routeProgress = chunkProgress.runtime_vehicles;
  const tripLoading =
    !error && (isCancelling || tripState?.loading || !tripState);
  const routeLoading =
    !error &&
    (isCancelling || runtimeState?.loading || !runtimeState);

  const metrics = useMemo(() => {
    const map = new Map();
    const ensure = (name) => {
      if (!map.has(name)) {
        map.set(name, {
          name,
          trips: 0,
          completed: 0,
          duration: 0,
          distance: 0,
          missed: 0,
          delay: 0,
          delayedTrips: 0,
          deviations: 0,
          coveredStops: 0,
          reportedTrips: 0,
          expectedStops: 0,
        });
      }
      return map.get(name);
    };

    routes.forEach((route, index) => {
      const item = ensure(routeName(route, index));
      item.expectedStops = getRouteStopCount(route);
    });
    trips.forEach((trip, index) => {
      const item = ensure(routeName(trip, index));
      item.trips += 1;
      item.completed += isOnTimeTrip(trip) ? 1 : 0;
      item.duration += getDuration(trip);
      item.missed += getMissedStops(trip);
      if (isDelayedTrip(trip)) {
        item.delay += getTripDelayMinutes(trip) || getDelay(trip);
        item.delayedTrips += 1;
      }
    });
    runtime.forEach((row, index) => {
      const item = ensure(routeName(row, index));
      item.distance += getDistance(row);
      item.deviations += number(
        row?.outOfRouteCount ?? row?.routeDeviationCount,
      );
      item.coveredStops += number(
        row?.noOfStopsCovered ?? row?.totalStopsCovered,
      );
      item.reportedTrips += number(
        row?.noOfTrips ?? row?.completedTrips,
      );
    });
    activity.forEach((row, index) => {
      const item = ensure(routeName(row, index));
      if (!item.deviations) {
        item.deviations += number(
          row?.outOfRouteCount ?? row?.routeDeviationCount,
        );
      }
    });

    const deviationRows = runtime.filter(
      (row) => number(row?.outOfRouteCount ?? row?.routeDeviationCount) > 0,
    );
    const routeRows = Array.from(map.values()).map((item) => {
      const tripCount = Math.max(item.trips, item.reportedTrips);
      const calculatedMissed = item.expectedStops
        ? Math.max(item.expectedStops * tripCount - item.coveredStops, 0)
        : 0;
      const route = routes.find(
        (candidate, index) => routeName(candidate, index) === item.name,
      );
      const establishedAdherence = route
        ? getRouteAdherencePercent(route, trips, deviationRows)
        : null;
      return {
        ...item,
        tripCount,
        missed: Math.max(item.missed, calculatedMissed),
        performance: item.trips
          ? Math.round((item.completed / item.trips) * 100)
          : 0,
        adherence:
          establishedAdherence ??
          (tripCount ? (item.deviations > 0 ? 0 : 100) : 0),
        utilization: tripCount
          ? Math.min(100, Math.round((item.trips / tripCount) * 100))
          : 0,
        avgDelay: item.delayedTrips ? item.delay / item.delayedTrips : 0,
      };
    });
    const activeRows = routeRows.filter((row) => row.trips > 0);
    const totalTrips = trips.length;
    const completed = trips.filter(isOnTimeTrip).length;
    const totalDuration = trips.reduce(
      (sum, trip) => sum + getDuration(trip),
      0,
    );
    const totalDistance = routeRows.reduce((sum, row) => sum + row.distance, 0);
    const missedStops = trips.reduce(
      (sum, trip) => sum + getMissedStops(trip),
      0,
    );
    const evaluatedRows = routeRows.filter((row) => row.tripCount > 0);
    const rowBasedAdherence = evaluatedRows.length
      ? Math.round(
          evaluatedRows.reduce((sum, row) => sum + row.adherence, 0) /
            evaluatedRows.length,
        )
      : 0;
    return {
      routeRows: routeRows.sort((a, b) => b.trips - a.trips),
      routes: routes.length || routeRows.length,
      onTime: totalTrips ? Math.round((completed / totalTrips) * 100) : 0,
      avgDuration: totalTrips ? totalDuration / totalTrips : 0,
      distance: totalDistance,
      missedStops: routeRows.reduce((sum, row) => sum + row.missed, 0) || missedStops,
      adherence: rowBasedAdherence,
      activeRows,
    };
  }, [routes, trips, runtime, activity]);

  const missedRows = metrics.routeRows
    .filter((row) => row.missed > 0)
    .slice(0, 5)
    .map((row, index) => ({
      ...row,
      fill: [COLORS.blue, COLORS.cyan, COLORS.purple, COLORS.amber, "#94A3B8"][
        index
      ],
    }));
  const missedTotal = missedRows.reduce((sum, row) => sum + row.missed, 0);
  const topRows = metrics.routeRows.filter((row) => row.trips > 0).slice(0, 6);
  const cardLoading = { routes: routeLoading, trips: tripLoading };
  const cards = [
    {
      title: "Routes",
      value: metrics.routes,
      icon: RouteOutlinedIcon,
      tone: "blue",
      source: "routes",
    },
    {
      title: "On-Time Performance",
      value: `${metrics.onTime}%`,
      icon: AccessTimeOutlinedIcon,
      tone: "green",
      source: "trips",
    },
    {
      title: "Avg. Duration",
      value: formatDuration(metrics.avgDuration),
      icon: TimerOutlinedIcon,
      tone: "amber",
      source: "trips",
    },
    {
      title: "Distance",
      value: `${Math.round(metrics.distance).toLocaleString()} km`,
      icon: AltRouteOutlinedIcon,
      tone: "purple",
      source: "routes",
    },
    {
      title: "Missed Stops",
      value: Math.round(metrics.missedStops),
      icon: WrongLocationOutlinedIcon,
      tone: "red",
      source: "trips",
    },
    {
      title: "Route Adherence",
      value: `${metrics.adherence}%`,
      icon: LocationOnOutlinedIcon,
      tone: "green",
      source: "routes",
    },
  ];

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {error && (
        <Typography sx={{ color: COLORS.red, fontSize: 12 }}>
          {error}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(6, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        {cards.map((card) => (
          <StatsCard
            key={card.title}
            title={t(card.title)}
            value={card.value}
            icon={card.icon}
            tone={card.tone}
            loading={cardLoading[card.source]}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "2fr 0.95fr 0.95fr" },
          gap: 2,
        }}
      >
        <Panel title={t("Route Map Overview")} progress={routeProgress}>
          <Box sx={{ height: 286, overflow: "hidden", borderRadius: 1 }}>
            <DashboardMapFilter
              filterBy="routes"
              compact
              showLiveIndicator={false}
              showStops={false}
              showVehicles={false}
            />
          </Box>
        </Panel>
        <Panel
          title={t("Route Performance")}
          action={t("VIEW ALL")}
          progress={tripProgress}
        >
          <ProgressList
            rows={topRows}
            valueKey="performance"
            color={COLORS.green}
          />
        </Panel>
        <Panel
          title={t("Route Adherence")}
          action={t("VIEW ALL")}
          progress={routeProgress}
        >
          <ProgressList
            rows={topRows}
            valueKey="adherence"
            color={COLORS.blue}
          />
        </Panel>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1fr 1.2fr" },
          gap: 2,
        }}
      >
        <Panel
          title={t("Route Utilization (%)")}
          action={t("View all")}
          progress={tripProgress}
        >
          <ProgressList
            rows={topRows}
            valueKey="utilization"
            color={COLORS.blue}
          />
        </Panel>
        <Panel
          title={t("Missed Stops by Route")}
          action={t("View All")}
          progress={tripProgress}
        >
          {missedRows.length ? (
            <Box sx={{ height: 230, display: "flex", alignItems: "center" }}>
              <Box sx={{ width: "58%", height: "100%", position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={missedRows}
                      dataKey="missed"
                      nameKey="name"
                      innerRadius="58%"
                      outerRadius="82%"
                      paddingAngle={1}
                      stroke="none"
                    >
                      {missedRows.map((row) => (
                        <Cell key={row.name} fill={row.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeContent: "center",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: 22 }}>
                    {Math.round(missedTotal)}
                  </Typography>
                  <Typography sx={{ color: COLORS.muted, fontSize: 9 }}>
                    {t("Total")}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1, display: "grid", gap: 1 }}>
                {missedRows.map((row) => (
                  <Typography
                    key={row.name}
                    sx={{ fontSize: 9, color: COLORS.text }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        mr: 0.6,
                        bgcolor: row.fill,
                      }}
                    />
                    {row.name}{" "}
                    <Box component="span" sx={{ color: COLORS.muted }}>
                      ({Math.round(row.missed)})
                    </Box>
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography
              sx={{
                py: 10,
                textAlign: "center",
                fontSize: 11,
                color: COLORS.muted,
              }}
            >
              No missed stops
            </Typography>
          )}
        </Panel>
        <Panel
          title={t("Avg. Delay by Route (mins)")}
          action={t("View All")}
          progress={tripProgress}
        >
          <ProgressList
            rows={topRows}
            valueKey="avgDelay"
            suffix=""
            color={COLORS.blue}
          />
        </Panel>
      </Box>
    </Box>
  );
}
